import { Fingerprint as FpIcon, HelpCircle, Users, Clock, TrendingUp, Chrome, Lock, Info } from 'lucide-react';
import { useFingerprints } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import { useState } from 'react';
import type { Fingerprint } from '../lib/api';

function InfoBox({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-8">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-blue-400 mb-1">{title}</h3>
          <p className="text-sm text-blue-300/80">{description}</p>
        </div>
      </div>
    </div>
  );
}

function FingerprintCard({ fingerprint }: { fingerprint: Fingerprint }) {
  const [expanded, setExpanded] = useState(false);
  
  // Determine threat level based on session count and recency
  const daysSinceLastSeen = Math.floor((Date.now() - new Date(fingerprint.lastSeen).getTime()) / (1000 * 60 * 60 * 24));
  const isActive = daysSinceLastSeen < 7;
  const isHighVolume = fingerprint.sessionCount > 10;
  
  const threatColor = isActive && isHighVolume 
    ? 'border-red-500/50 bg-red-500/5'
    : isActive 
    ? 'border-orange-500/50 bg-orange-500/5'
    : 'border-gray-700 bg-gray-900';

  return (
    <div className={`border rounded-lg overflow-hidden ${threatColor}`}>
      {/* Compact Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <FpIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-white">
                    {fingerprint.browserFamily || 'Unknown Client'}
                  </span>
                  {isActive && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {fingerprint.osFamily || 'Unknown OS'} • {fingerprint.sessionCount} sessions
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last seen {daysSinceLastSeen === 0 ? 'today' : `${daysSinceLastSeen}d ago`}
              </div>
              {fingerprint.clusterIds && fingerprint.clusterIds.length > 0 && (
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {fingerprint.clusterIds.length} cluster{fingerprint.clusterIds.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
          
          <button className="text-xs text-blue-400 hover:text-blue-300">
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-800 p-4 space-y-4">
          {/* What This Means */}
          <div>
            <div className="text-xs text-gray-500 mb-2 font-medium">FINGERPRINT ANALYSIS</div>
            <p className="text-sm text-gray-400 leading-relaxed">
              This unique fingerprint identifies a specific tool or browser configuration. 
              {fingerprint.sessionCount > 5 
                ? ` It has been seen ${fingerprint.sessionCount} times, suggesting ${fingerprint.sessionCount > 20 ? 'persistent scanning activity' : 'repeated reconnaissance'}.`
                : ' This is a relatively new or infrequent visitor.'}
              {isActive && ' This fingerprint is currently active.'}
            </p>
          </div>

          {/* Technical Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-gray-500 font-medium">JA3 HASH (TLS)</span>
              </div>
              <code className="text-xs text-purple-300 bg-gray-900 px-2 py-1 rounded block overflow-x-auto">
                {fingerprint.ja3Hash || 'Not available'}
              </code>
              <p className="text-xs text-gray-600 mt-1">
                Identifies SSL/TLS client behavior
              </p>
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Chrome className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-gray-500 font-medium">JA4 HASH (HTTP/2)</span>
              </div>
              <code className="text-xs text-blue-300 bg-gray-900 px-2 py-1 rounded block overflow-x-auto">
                {fingerprint.ja4Hash || 'Not available'}
              </code>
              <p className="text-xs text-gray-600 mt-1">
                Newer HTTP/2 fingerprinting method
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-800 text-xs text-gray-500">
            <div>
              <span className="text-gray-600">First seen:</span> {new Date(fingerprint.firstSeen).toLocaleString()}
            </div>
            <div>
              <span className="text-gray-600">Last seen:</span> {new Date(fingerprint.lastSeen).toLocaleString()}
            </div>
          </div>
          
          {/* Clustering Info */}
          {fingerprint.clusterIds && fingerprint.clusterIds.length > 0 && (
            <div className="pt-3 border-t border-gray-800">
              <div className="text-xs text-gray-500 mb-2 font-medium">BEHAVIORAL CLUSTERS</div>
              <div className="flex flex-wrap gap-2">
                {fingerprint.clusterIds.map((clusterId) => (
                  <span key={clusterId} className="px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded font-mono">
                    {clusterId.slice(0, 8)}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Associated with {fingerprint.clusterIds.length} attack pattern{fingerprint.clusterIds.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Fingerprints() {
  const { isConnected } = useAuth();
  const { data, isLoading } = useFingerprints();

  const fingerprints = data?.fingerprints ?? [];
  
  // Sort by last seen (most recent first)
  const sortedFingerprints = [...fingerprints].sort((a, b) => 
    new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime()
  );

  // Calculate stats
  const activeFPs = sortedFingerprints.filter(fp => {
    const daysSince = Math.floor((Date.now() - new Date(fp.lastSeen).getTime()) / (1000 * 60 * 60 * 24));
    return daysSince < 7;
  }).length;
  
  const totalSessions = sortedFingerprints.reduce((sum, fp) => sum + fp.sessionCount, 0);
  const uniqueTools = new Set(sortedFingerprints.map(fp => fp.browserFamily).filter(Boolean)).size;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FpIcon className="w-7 h-7 text-blue-400" />
          <h2 className="text-2xl font-bold text-white">Fingerprint Intelligence</h2>
        </div>
        <p className="text-gray-400">
          Unique client fingerprints tracked across all sessions
        </p>
      </div>

      {/* Educational Info Box */}
      <InfoBox 
        title="What are Fingerprints?"
        description="Fingerprints are unique identifiers created from SSL/TLS and HTTP characteristics. They help track attackers even when they change IP addresses or use VPNs. JA3 fingerprints TLS handshakes, while JA4 analyzes HTTP/2 patterns. Each fingerprint represents a specific tool, browser, or attack framework."
      />

      {!isConnected ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <FpIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane in Settings to view fingerprints</p>
        </div>
      ) : fingerprints.length === 0 ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <FpIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">
            {isLoading ? 'Loading fingerprints...' : 'No fingerprints collected yet'}
          </p>
          <p className="text-gray-600 text-sm">
            Fingerprints appear once agents start collecting TLS/HTTP telemetry from attackers
          </p>
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-sg-card border border-sg-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <FpIcon className="w-5 h-5 text-blue-400" />
                <div className="text-2xl font-bold text-white">{fingerprints.length}</div>
              </div>
              <div className="text-sm text-gray-400">Unique Fingerprints</div>
            </div>
            
            <div className="bg-sg-card border border-sg-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <div className="text-2xl font-bold text-white">{activeFPs}</div>
              </div>
              <div className="text-sm text-gray-400">Active (7 days)</div>
            </div>
            
            <div className="bg-sg-card border border-sg-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-purple-400" />
                <div className="text-2xl font-bold text-white">{totalSessions}</div>
              </div>
              <div className="text-sm text-gray-400">Total Sessions</div>
            </div>
          </div>

          {/* Fingerprint Cards */}
          <div className="space-y-3">
            {sortedFingerprints.map((fingerprint) => (
              <FingerprintCard key={fingerprint.id} fingerprint={fingerprint} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
